import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import { useAuthContext } from '../../context/auth-context';
import {
  useGetEventPerformanceQuery,
  useCalculatePerformanceMutation,
  useGetCalculateStatusQuery,
} from '../../redux/services/report-api';
import { PageHeader } from '../../components/shared';

const SEMAFORO = { RED_MAX: 60, YELLOW_MAX: 80 }

const getColorByScore = (score) => {
  if (score === null || score === undefined) return '#9e9e9e'
  if (score < SEMAFORO.RED_MAX)    return '#c62828'
  if (score < SEMAFORO.YELLOW_MAX) return '#e65100'
  return '#2e7d32'
}

const getChipProps = (score) => {
  if (score === null || score === undefined) return { label: 'Sin datos', sx: { backgroundColor: '#f0f0f0', color: '#757575' } }
  if (score < SEMAFORO.RED_MAX)    return { label: 'Crítico',  sx: { backgroundColor: '#FDECEA', color: '#b71c1c', fontWeight: 700 } }
  if (score < SEMAFORO.YELLOW_MAX) return { label: 'Regular',  sx: { backgroundColor: '#FFF3E0', color: '#e65100', fontWeight: 700 } }
  return { label: 'Bueno', sx: { backgroundColor: '#E6F4EA', color: '#1b5e20', fontWeight: 700 } }
}

const ScoreBar = ({ label, value, max }) => {
  const pct = max ? Math.min((value / max) * 100, 100) : 0
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1, color: 'text.secondary', fontSize: '0.8rem' }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          {value?.toFixed(1) ?? '—'}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 7,
          borderRadius: 4,
          backgroundColor: 'rgba(40,75,50,0.1)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: getColorByScore(value),
            borderRadius: 4
          },
        }}
      />
    </Box>
  )
}

const EventCard = ({ snapshot, selected, onClick }) => {
  const score = snapshot.avgScore
  const chip  = getChipProps(score)
  const color = getColorByScore(score)

  return (
    <Card
      onClick={onClick}
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        outline: selected ? `2px solid #284B32` : 'none',
        outlineOffset: selected ? '-2px' : 0,
        transition: 'outline 0.15s',
      }}
    >
      <CardActionArea sx={{ p: 0 }}>
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, flex: 1 }}>
              {snapshot.event?.name ?? '—'}
            </Typography>
            <Chip size="small" {...chip} sx={{ ...chip.sx, fontSize: '0.68rem', height: 20 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {score !== null && score !== undefined ? score.toFixed(1) : 'Sin datos'}
          </Typography>
        </Box>
        {/* score bar strip at bottom */}
        <Box sx={{
          height: 3,
          backgroundColor: color,
          width: score != null ? `${Math.min(score, 100)}%` : '0%',
          borderRadius: '0 0 0 0',
          transition: 'width 0.4s ease',
        }} />
      </CardActionArea>
    </Card>
  )
}

const DrillDown = ({ snapshot }) => {
  if (!snapshot) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
      <Typography color="text.disabled">Selecciona un evento para ver el detalle.</Typography>
    </Box>
  )

  const maxRubric = snapshot.rubricBreakdown?.length
    ? Math.max(...snapshot.rubricBreakdown.map(r => r.avg))
    : 100

  const maxClub = snapshot.clubBreakdown?.length
    ? Math.max(...snapshot.clubBreakdown.map(c => c.avg))
    : 100

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#284B32', flex: 1 }}>
          {snapshot.event?.name}
        </Typography>
        <Chip
          label={`Prom. ${snapshot.avgScore?.toFixed(1) ?? '—'}`}
          sx={{
            backgroundColor: getColorByScore(snapshot.avgScore),
            color: '#fff',
            fontWeight: 700
          }}
        />
      </Box>

      {snapshot.rubricBreakdown?.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#284B32', mb: 1 }}>
            Por rúbrica
          </Typography>
          {snapshot.rubricBreakdown
            .slice()
            .sort((a, b) => b.avg - a.avg)
            .map((r, i) => (
              <ScoreBar key={i} label={r.name} value={r.avg} max={maxRubric || 100} />
            ))}
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {snapshot.clubBreakdown?.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#284B32', mb: 1 }}>
            Por club
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Club</TableCell>
                  <TableCell align="right">Puntaje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snapshot.clubBreakdown
                  .slice()
                  .sort((a, b) => b.avg - a.avg)
                  .map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Typography variant="body2">{c.name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Box sx={{ flex: 1, maxWidth: 80 }}>
                            <LinearProgress
                              variant="determinate"
                              value={maxClub ? Math.min((c.avg / maxClub) * 100, 100) : 0}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(40,75,50,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: getColorByScore(c.avg),
                                  borderRadius: 3,
                                },
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 40, textAlign: 'right' }}>
                            {c.avg.toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!snapshot.rubricBreakdown?.length && !snapshot.clubBreakdown?.length && (
        <Typography color="text.disabled" variant="body2">Sin datos de detalle.</Typography>
      )}
    </Box>
  )
}

const EventPerformanceDashboard = () => {
  const { activeCamp } = useAuthContext()
  const campId = activeCamp?._id

  const [selected, setSelected]         = useState(null)
  const [calculating, setCalculating]   = useState(false)
  const [doneFlash, setDoneFlash]       = useState(false)
  const pollingRef = useRef(null)

  const { data, isLoading, refetch } = useGetEventPerformanceQuery(
    { campId },
    { skip: !campId }
  )

  const [triggerCalculate] = useCalculatePerformanceMutation()

  const { data: statusData, refetch: refetchStatus } = useGetCalculateStatusQuery(
    { campId },
    { skip: !campId || !calculating }
  )

  // Start polling when calculating
  useEffect(() => {
    if (!calculating) return
    pollingRef.current = setInterval(async () => {
      const result = await refetchStatus()
      if (!result.data?.calculating) {
        setCalculating(false)
        setDoneFlash(true)
        setTimeout(() => setDoneFlash(false), 3000)
        refetch()
        clearInterval(pollingRef.current)
      }
    }, 2000)
    return () => clearInterval(pollingRef.current)
  }, [calculating, refetchStatus, refetch])

  // Sync initial calculating state from server
  useEffect(() => {
    if (data?.calculating) setCalculating(true)
  }, [data?.calculating])

  const handleCalculate = useCallback(async () => {
    if (!campId || calculating) return
    try {
      await triggerCalculate({ campId }).unwrap()
      setCalculating(true)
    } catch (err) {
      if (err?.status === 409) {
        setCalculating(true)
      }
    }
  }, [campId, calculating, triggerCalculate])

  const snapshots = data?.snapshots ?? []

  // Summary metrics
  const total     = snapshots.length
  const withData  = snapshots.filter(s => s.avgScore != null)
  const globalAvg = withData.length
    ? withData.reduce((a, s) => a + s.avgScore, 0) / withData.length
    : null
  const critical  = withData.filter(s => s.avgScore < SEMAFORO.RED_MAX).length
  const noData    = total - withData.length

  const lastCalc = data?.lastCalculated
    ? new Date(data.lastCalculated).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' })
    : null

  const selectedSnapshot = selected != null ? snapshots[selected] : null

  const metricCards = [
    { label: 'Total eventos',      value: total,                    color: '#284B32' },
    { label: 'Promedio global',    value: globalAvg?.toFixed(1) ?? '—', color: '#1b5e20' },
    { label: 'Críticos (<60)',     value: critical,                 color: '#c62828' },
    { label: 'Sin calificación',   value: noData,                   color: '#9e9e9e' },
  ]

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ pl: 2, borderLeft: '4px solid #284B32' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2 }}>
            Performance de eventos
          </Typography>
          {lastCalc && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Última actualización: {lastCalc}
            </Typography>
          )}
        </Box>

        {doneFlash ? (
          <Button variant="outlined" color="success" startIcon={<CheckIcon />} disabled>
            Listo
          </Button>
        ) : calculating ? (
          <Button variant="outlined" disabled startIcon={<CircularProgress size={16} />}>
            Calculando...
          </Button>
        ) : (
          <Tooltip title="Recalcula los promedios de todos los eventos del campamento">
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleCalculate}
              disabled={!campId}
              sx={{ backgroundColor: '#0F6E56', '&:hover': { backgroundColor: '#0a5a45' } }}
            >
              Recalcular resultados
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Metric cards */}
      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        {metricCards.map(m => (
          <Card key={m.label} sx={{ flex: '1 1 140px', minWidth: 0 }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{m.label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: m.color, lineHeight: 1.2 }}>
                {m.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {!campId && (
        <Typography color="text.secondary">Selecciona un campamento para ver el dashboard.</Typography>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: '#284B32' }} />
        </Box>
      )}

      {snapshots.length === 0 && !isLoading && campId && (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Sin datos. Haz clic en <strong>Recalcular resultados</strong> para calcular el performance de los eventos.
          </Typography>
        </Card>
      )}

      {snapshots.length > 0 && (
        <Grid container spacing={3}>
          {/* Left — event cards */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600 }}>
              {total} eventos
            </Typography>
            {snapshots.map((s, i) => (
              <EventCard
                key={s._id}
                snapshot={s}
                selected={selected === i}
                onClick={() => setSelected(i)}
              />
            ))}
          </Grid>

          {/* Right — drill-down */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2.5, minHeight: 300 }}>
              <DrillDown snapshot={selectedSnapshot} />
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  )
}

export default EventPerformanceDashboard
